import React, { useState, ChangeEvent } from 'react';
import {
  Container,
  Typography,
  Grid,
  Box,
  Button,
  Paper,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { fetchApi } from './fetchApi';

interface ImportSummary {
  totalRows: number;
  created: number;
  updated: number;
  errors: number;
}

interface ImportError {
  row: number | string;
  message: string;
}

interface ImportResponse {
  success: boolean;
  summary?: ImportSummary;
  errors?: ImportError[];
  message?: string;
  error?: string;
}

export default function ImportSeite() {
  const [artikelFile, setArtikelFile] = useState<File | null>(null);
  const [artikelLoading, setArtikelLoading] = useState(false);
  const [artikelSummary, setArtikelSummary] = useState<ImportSummary | null>(null);
  const [artikelErrors, setArtikelErrors] = useState<ImportError[]>([]);
  const [artikelInfo, setArtikelInfo] = useState<string | null>(null);

  const handleDownloadTemplate = async () => {
    try {
      const res = await fetchApi('/api/import/template/artikel');
      if (!res.ok) {
        throw new Error('Fehler beim Download des Artikel-Templates.');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      const a = document.createElement('a');
      a.href = url;
      a.download = 'artikel_import_template.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setArtikelInfo(err.message || 'Fehler beim Download des Artikel-Templates.');
    }
  };

  const handleArtikelFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setArtikelFile(file);
    setArtikelSummary(null);
    setArtikelErrors([]);
    setArtikelInfo(null);
  };

  const uploadCsv = async () => {
    if (!artikelFile) {
      setArtikelInfo('Bitte zuerst eine CSV-Datei auswählen.');
      return;
    }

    const formData = new FormData();
    formData.append('file', artikelFile);

    setArtikelLoading(true);
    setArtikelSummary(null);
    setArtikelErrors([]);
    setArtikelInfo(null);

    try {
      const res = await fetchApi('/api/import/artikel', {
        method: 'POST',
        body: formData,
      });

      const data: ImportResponse = await res.json();

      if (!res.ok || !data.success) {
        const msg =
          data.message ||
          data.error ||
          'Artikel-Import ist fehlgeschlagen.';
        setArtikelInfo(msg);
        setArtikelSummary(data.summary || null);
        setArtikelErrors(data.errors || []);
      } else {
        setArtikelInfo('Artikel-Import erfolgreich abgeschlossen.');
        setArtikelSummary(data.summary || null);
        setArtikelErrors([]);
      }
    } catch (err: any) {
      const msg =
        err?.message === '401'
          ? 'Session abgelaufen. Bitte neu einloggen.'
          : (err?.message || 'Unbekannter Fehler beim Artikel-Import.');
      setArtikelInfo(msg);
    } finally {
      setArtikelLoading(false);
    }
  };

  const renderSummary = (summary: ImportSummary | null) => {
    if (!summary) return null;

    return (
      <Box mt={2}>
        <Typography variant="subtitle1">Zusammenfassung:</Typography>
        <Typography variant="body2">
          Gesamtzeilen: {summary.totalRows}
        </Typography>
        <Typography variant="body2">
          Neu angelegt: {summary.created}
        </Typography>
        <Typography variant="body2">
          Aktualisiert: {summary.updated}
        </Typography>
        <Typography variant="body2">
          Fehler: {summary.errors}
        </Typography>
      </Box>
    );
  };

  const renderErrors = (errors: ImportError[]) => {
    if (!errors || errors.length === 0) return null;

    const maxShow = 50;
    const slice = errors.slice(0, maxShow);

    return (
      <Box mt={2}>
        <Alert severity="error" variant="outlined">
          <Typography variant="subtitle1">
            Fehler ({errors.length}):
          </Typography>
          <List dense>
            {slice.map((err, idx) => (
              <ListItem key={idx} disableGutters>
                <ListItemText
                  primary={
                    typeof err.row !== 'undefined'
                      ? `Zeile ${err.row}: ${err.message}`
                      : err.message
                  }
                />
              </ListItem>
            ))}
          </List>
          {errors.length > maxShow && (
            <Typography variant="caption">
              Es werden nur die ersten {maxShow} Fehler angezeigt.
            </Typography>
          )}
        </Alert>
      </Box>
    );
  };

  return (
    <Container maxWidth="md">
      <Box mt={4} mb={4}>
        <Typography variant="h4" gutterBottom>
          CSV-Import – Artikel
        </Typography>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            Schritt 1: Lade zuerst das Template herunter und fülle es in Excel.
            Die Header müssen exakt so bleiben wie im Template vorgegeben.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Schritt 2: Speichere die Datei als CSV (mit &quot;;&quot; als
            Trennzeichen) und lade sie hier wieder hoch.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            <strong>Wichtig:</strong> Parent-Artikel (ohne parent_sku) immer oberhalb
            ihrer Child-Artikel (mit parent_sku) in der CSV anordnen.
          </Typography>
        </Alert>

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Artikel-Import
              </Typography>

              <Box mb={2}>
                <Typography variant="body2">
                  Template-Header (zur Erinnerung):
                </Typography>
                <Typography
                  variant="caption"
                  component="pre"
                  sx={{
                    display: 'block',
                    whiteSpace: 'normal',
                    wordBreak: 'break-all',
                    bgcolor: '#f5f5f5',
                    p: 1,
                    mt: 0.5,
                  }}
                >
                  sku*;parent_sku;bezeichnung*;marke;beschreibung;farbe;groesse;ean;asin;fnsku;amazon_sku;amazon_product_id;shopify_product_id;shopify_variant_id;barcode_intern;einkaufspreis;verkaufspreis;mindestbestand;lieferant_firmenname;kategorie_name;bild_url
                </Typography>
              </Box>

              <Box display="flex" flexDirection="row" gap={2} mb={2}>
                <Button
                  variant="outlined"
                  onClick={handleDownloadTemplate}
                >
                  Artikel-Template herunterladen
                </Button>

                <label>
                  <input
                    type="file"
                    accept=".csv,text/csv"
                    style={{ display: 'none' }}
                    onChange={handleArtikelFileChange}
                  />
                  <Button variant="contained" component="span">
                    CSV wählen
                  </Button>
                </label>

                <Button
                  variant="contained"
                  color="success"
                  onClick={uploadCsv}
                  disabled={artikelLoading || !artikelFile}
                >
                  Artikel-CSV importieren
                </Button>
              </Box>

              {artikelFile && (
                <Typography variant="body2">
                  Ausgewählte Datei: {artikelFile.name}
                </Typography>
              )}

              {artikelLoading && (
                <Box mt={2}>
                  <LinearProgress />
                  <Typography variant="body2" mt={1}>
                    Import läuft …
                  </Typography>
                </Box>
              )}

              {artikelInfo && (
                <Box mt={2}>
                  <Alert severity="info">{artikelInfo}</Alert>
                </Box>
              )}

              {renderSummary(artikelSummary)}
              {renderErrors(artikelErrors)}
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
}
